import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DateAdapter } from '@myphx-engineering/date-utils/client';
import { createFormContext } from '@myphx-engineering/phx-design-system/hooks';

type BasicDetails = {
    day: string;
    month: string;
    year: string;
};

export const useDobValidation = <T extends BasicDetails>(
    form: ReturnType<ReturnType<typeof createFormContext<T>>[1]>
) => {
    const { t } = useTranslation();

    useEffect(() => {
        const day = Number.parseInt(form.values.day, 10);
        const month = Number.parseInt(form.values.month, 10);
        const year = Number.parseInt(form.values.year, 10);
        if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
            form.setFieldError('dob', t('common.formErrors.dob.required'));

            return;
        }

        if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) {
            form.setFieldError('dob', t('common.formErrors.dob.invalid'));

            return;
        }

        // @ TODO add versions of isValidDate, isFutureDate, and validateAge that accept a simple date string so we don't have to explicitly parse the value and pass in separate Y/M/D values.
        const isValid = DateAdapter.isValidDate(year, month, day);

        if (!isValid) {
            form.setFieldError('dob', t('common.formErrors.dob.invalid'));
            return;
        }

        // @ TODO add versions of isValidDate, isFutureDate, and validateAge that accept a simple date string so we don't have to explicitly parse the value and pass in separate Y/M/D values.
        const isFuture = DateAdapter.isFutureDate(year, month, day);

        if (isFuture) {
            form.setFieldError('dob', t('common.formErrors.dob.future'));

            return;
        }

        // @ TODO add versions of isValidDate, isFutureDate, and validateAge that accept a simple date string so we don't have to explicitly parse the value and pass in separate Y/M/D values.
        const isValidAge = DateAdapter.validateAge(year, month, day, 13);

        if (!isValidAge) {
            form.setFieldError('dob', t('common.formErrors.dob.tooYoung'));
            return;
        }

        form.clearFieldError('dob');
    }, [form, t]);
};
